import {
  MoreItem,
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import {
  AHTeamIconCircle,
  AHTeamIconType,
  AHText,
  Box,
  Input,
  ListItem,
} from "arkh";
import React, { useLayoutEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { UserAdminEditGroupDeviceScreenQuery } from "./__generated__/UserAdminEditGroupDeviceScreenQuery.graphql";
import { UserAdminEditGroupDeviceScreenMutation } from "./__generated__/UserAdminEditGroupDeviceScreenMutation.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useAlert } from "@/src/common/Alert";
import { UserAdminEditGroupDeviceSetTeamsScreenMutation } from "./__generated__/UserAdminEditGroupDeviceSetTeamsScreenMutation.graphql";
import { UserAdminEditGroupDeviceSetTeamsScreenQuery } from "./__generated__/UserAdminEditGroupDeviceSetTeamsScreenQuery.graphql";
import {
  TeamSelectList,
  TeamSelectListWholeStoreOption,
} from "widgets/src/TeamSelectList";
function InnerUserAdminEditGroupDeviceScreen(props: {
  fetchKey: string;
  route: RouteProp<ParamList, "UserAdminEditGroupDeviceScreen">;
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "UserAdminEditGroupDeviceScreen">,
    UserLoggedInNavigatorNavigationProp<"GroupDeviceEdit">
  >;
}) {
  const data = useLazyLoadQuery<UserAdminEditGroupDeviceScreenQuery>(
    graphql`
      query UserAdminEditGroupDeviceScreenQuery($id: ID!, $storeId: ID) {
        store(id: $storeId) {
          sharedMobileDevice(id: $id) {
            id
            name
            teams {
              id
              name
              icon
              color
              isPrivate
            }
          }
        }
      }
    `,
    {
      id: props.route.params.id,
      storeId: props.route.params.storeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [save, saveLoading] =
    useMutation<UserAdminEditGroupDeviceScreenMutation>(graphql`
      mutation UserAdminEditGroupDeviceScreenMutation(
        $id: ID!
        $name: String!
        $storeId: ID
      ) {
        store(id: $storeId) {
          sharedMobileDevice(id: $id) {
            update(name: $name) {
              id
            }
          }
        }
      }
    `);

  const [deleteDevice,] = useMutation(graphql`
    mutation UserAdminEditGroupDeviceScreenDeleteMutation(
      $id: ID!
      $storeId: ID
    ) {
      store(id: $storeId) {
        sharedMobileDevice(id: $id) {
          delete {
            id
          }
        }
      }
    }
  `);

  const remoteName = useMemo(() => {
    return data.store.sharedMobileDevice?.name;
  }, [data.store.sharedMobileDevice?.name]);
  const [name, setName] = useState(remoteName);
  const intl_tokens = useIntlTokens();
  const getOptions = useNavigationHeaderOptions();
  const alert = useAlert();
  useLayoutEffect(() => {
    const more: MoreItem[] = [
      {
        icon: "trash",
        color: "negative",
        label: intl_tokens.Delete,
        onPress: () => {
          alert.alert(`Delete ${data.store.sharedMobileDevice?.name}`, "", [
            {
              text: intl_tokens.Cancel,
              onPress: () => {},
              style: "cancel",
            },
            {
              text: intl_tokens.Delete,
              onPress: () => {
                deleteDevice({
                  variables: {
                    id: props.route.params.id,
                    storeId: props.route.params.storeId,
                  },
                  onCompleted: () => {
                    props.navigation.goBack();
                  },
                });
              },
              style: "destructive",
            },
          ]);
        },
      },
    ];
    if (remoteName !== name) {
      props.navigation.setOptions(
        getOptions({
          type: "SAVE",
          saveLoading,
          onSave: () => {
            save({
              variables: {
                id: props.route.params.id,
                name: name ?? "",
                storeId: props.route.params.storeId,
              },
              onCompleted: () => {
                props.navigation.goBack();
              },
            });
          },
          onCancel: () => {
            props.navigation.goBack();
          },
          more,
        })
      );
    } else {
      props.navigation.setOptions(
        getOptions({
          type: "CLOSE",
          onClose: () => {
            props.navigation.goBack();
          },
          more,
        })
      );
    }
  }, [
    props.navigation,
    getOptions,
    name,
    props.route.params.id,
    save,
    deleteDevice,
    intl_tokens.Delete,
    alert,
    data.store.sharedMobileDevice?.name,
    intl_tokens.Cancel,
    saveLoading,
    remoteName,
    props.route.params.storeId,
  ]);

  const teams = data.store?.sharedMobileDevice?.teams ?? [];
  return (
    <Box space="medium" color="surface-primary" style={{ flex: 1 }}>
      <AHText type="title-small" spaceBottom="small">
        {intl_tokens.Name}
      </AHText>
      <Input
        value={name}
        onChangeText={(text) => {
          setName(text);
        }}
      />
      <Box space="medium"></Box>
      <AHText type="title-small" spaceBottom="small">
        {intl_tokens.Teams}
      </AHText>
      <ListItem
        border="border-primary"
        onPress={() => {
          props.navigation.navigateDeprecated(
            "UserAdminEditGroupDeviceSetTeamsScreen",
            {
              id: props.route.params.id,
            }
          );
        }}
        isFirst={true}
        isLast={true}
        right={<ListItem.NavArrow />}
      >
        <AHText type="subtitle">
          {teams.length > 0 ? (
            teams.map((t) => (
              <Item
                key={t.id}
                id={t.id}
                name={t.name}
                color={t.color}
                icon={t.icon ?? null}
                isPrivate={t.isPrivate}
              />
            ))
          ) : (
            <AHText numberOfLines={1} type="subtitle">
              {intl_tokens.AllTeams}
            </AHText>
          )}
        </AHText>
      </ListItem>
    </Box>
  );
}

function Item(props: {
  id: string;
  name: string;
  color: string | null;
  icon: AHTeamIconType | null;
  compact?: boolean;
  isPrivate: boolean;
}) {
  return (
    <Box
      key={props.id}
      style={{ flexDirection: "row", alignItems: "center" }}
      spaceRight="small"
    >
      <AHTeamIconCircle
        size="small"
        color={props.color}
        type={props.icon}
        isPrivate={props.isPrivate}
      />
      {!props.compact ? (
        <AHText spaceLeft="xsmall" color="text-primary" type="subtitle">
          {props.name}
        </AHText>
      ) : null}
    </Box>
  );
}

function UserAdminEditGroupDeviceScreen(props: {
  route: RouteProp<ParamList, "UserAdminEditGroupDeviceScreen">;
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "UserAdminEditGroupDeviceScreen">,
    UserLoggedInNavigatorNavigationProp<"GroupDeviceEdit">
  >;
}) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerUserAdminEditGroupDeviceScreen
          fetchKey={fetchKey}
          route={props.route}
          navigation={props.navigation}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerUserAdminEditGroupDeviceSetTeamsScreen(props: {
  fetchKey: string;
  route: RouteProp<ParamList, "UserAdminEditGroupDeviceSetTeamsScreen">;
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<
      ParamList,
      "UserAdminEditGroupDeviceSetTeamsScreen"
    >,
    UserLoggedInNavigatorNavigationProp<"GroupDeviceEdit">
  >;
}) {
  const data = useLazyLoadQuery<UserAdminEditGroupDeviceSetTeamsScreenQuery>(
    graphql`
      query UserAdminEditGroupDeviceSetTeamsScreenQuery($id: ID!) {
        store {
          publicTeams {
            ...TeamSelectList_Team
          }
          sharedMobileDevice(id: $id) {
            id
            name
            teams {
              id
              name
            }
          }
        }
      }
    `,
    { id: props.route.params.id },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [save, saveLoading] =
    useMutation<UserAdminEditGroupDeviceSetTeamsScreenMutation>(graphql`
      mutation UserAdminEditGroupDeviceSetTeamsScreenMutation(
        $id: ID!
        $teamIds: [ID!]!
      ) {
        store {
          sharedMobileDevice(id: $id) {
            setTeams(teamIds: $teamIds) {
              id
              teams {
                id
                name
              }
            }
          }
        }
      }
    `);

  const intl_tokens = useIntlTokens();

  const [selected, setSelected] = useState<string[]>(
    data.store.sharedMobileDevice?.teams.map((team) => team.id) ?? []
  );

  useNavigationHeader({
    type: "SAVE",
    title: intl_tokens.Teams,
    saveLoading,
    onSave() {
      save({
        variables: { id: props.route.params.id, teamIds: selected },
        onCompleted: () => {
          props.navigation.goBack();
        },
      });
    },
    onCancel() {
      props.navigation.goBack();
    },
  });

  return (
    <Box spaceTop="medium" style={{ flex: 1 }}>
      <TeamSelectList
        teams={data.store.publicTeams}
        ListHeaderComponent={
          <TeamSelectListWholeStoreOption
            onSelect={() => {
              if (selected.length === 0) {
              } else {
                setSelected([]);
              }
            }}
            selected={selected.length === 0}
          />
        }
        onItemPress={(itemId, selected) => {
          setSelected((prev) => {
            if (selected) {
              return prev.filter((id) => id !== itemId);
            }
            return [...prev, itemId];
          });
        }}
        selected={selected}
        insets={{}}
      />
    </Box>
  );
}

function UserAdminEditGroupDeviceSetTeamsScreen(props: {
  route: RouteProp<ParamList, "UserAdminEditGroupDeviceSetTeamsScreen">;
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<
      ParamList,
      "UserAdminEditGroupDeviceSetTeamsScreen"
    >,
    UserLoggedInNavigatorNavigationProp<"GroupDeviceEdit">
  >;
}) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerUserAdminEditGroupDeviceSetTeamsScreen
          fetchKey={fetchKey}
          route={props.route}
          navigation={props.navigation}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

type ParamList = {
  UserAdminEditGroupDeviceSetTeamsScreen: { id: string };
  UserAdminEditGroupDeviceScreen: { id: string; storeId?: string };
};

const Stack = createNativeStackNavigator<ParamList>();

export const UserAdminEditGroupDevice = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"GroupDeviceEdit">) => {
    const getOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="UserAdminEditGroupDeviceScreen"
          component={UserAdminEditGroupDeviceScreen}
          initialParams={{
            id: props.route.params.id,
            storeId: props.route.params.storeId,
          }}
          options={getOptions({
            title: "",
            type: "CLOSE",
            onClose: () => {
              props.navigation.goBack();
            },
          })}
        />
        <Stack.Screen
          name="UserAdminEditGroupDeviceSetTeamsScreen"
          component={UserAdminEditGroupDeviceSetTeamsScreen}
          initialParams={{ id: props.route.params.id }}
          options={getOptions({
            title: "",
            type: "CLOSE",
            onClose: () => {
              props.navigation.goBack();
            },
          })}
        />
      </Stack.Navigator>
    );
  }
);
